<template>
    <div class="page-title">
        <div class="title">
            <h3>Quotation Pricing Settings</h3>
        </div>
    </div>

    <div class="card mb-3">
        <div class="card-body">
            <el-alert
                title="These percentages apply to new and edited quotations. Changes are logged."
                type="info"
                :closable="false"
                show-icon
                class="mb-3"
            />

            <div v-loading="loading">
                <h5 class="mb-3">Land</h5>
                <el-form label-position="top">
                    <el-row :gutter="16">
                        <el-col
                            v-for="item in landSettings"
                            :key="item.key"
                            :xs="24"
                            :sm="12"
                            :md="8"
                            :lg="6"
                        >
                            <el-form-item :label="item.label">
                                <el-input-number
                                    v-model="item.value"
                                    :min="0"
                                    :max="100"
                                    :precision="2"
                                    :step="0.5"
                                    controls-position="right"
                                    style="width: 100%"
                                />
                            </el-form-item>
                        </el-col>
                    </el-row>
                </el-form>

                <h5 class="mb-3 mt-2">Flight</h5>
                <el-form label-position="top">
                    <el-row :gutter="16">
                        <el-col
                            v-for="item in flightSettings"
                            :key="item.key"
                            :xs="24"
                            :sm="12"
                            :md="8"
                            :lg="6"
                        >
                            <el-form-item :label="item.label">
                                <el-input-number
                                    v-model="item.value"
                                    :min="0"
                                    :max="100"
                                    :precision="2"
                                    :step="0.5"
                                    controls-position="right"
                                    style="width: 100%"
                                />
                            </el-form-item>
                        </el-col>
                    </el-row>
                </el-form>

                <h5 class="mb-3 mt-2">Addon Automation</h5>
                <el-alert
                    class="mb-3"
                    type="info"
                    :closable="false"
                    show-icon
                >
                    <p class="mb-1"><strong>Transport / Puja:</strong> units = ceil(adults ÷ capacity). Example capacity 4 → 1–4 adults = 1×, 5–8 = 2×, 9–12 = 3×.</p>
                    <p class="mb-0"><strong>Darshan:</strong> same formula; capacity 1 means total = price × adults (1=x, 2=2x, …).</p>
                </el-alert>
                <el-form label-position="top">
                    <el-row :gutter="16">
                        <el-col
                            v-for="item in automationSettings"
                            :key="item.key"
                            :xs="24"
                            :sm="12"
                            :md="8"
                            :lg="6"
                        >
                            <el-form-item :label="item.label">
                                <el-input-number
                                    v-model="item.value"
                                    :min="1"
                                    :max="50"
                                    :precision="0"
                                    :step="1"
                                    controls-position="right"
                                    style="width: 100%"
                                />
                            </el-form-item>
                        </el-col>
                    </el-row>
                </el-form>

                <h5 class="mb-3 mt-2">Tax application</h5>
                <el-form label-position="top">
                    <el-row :gutter="16">
                        <el-col
                            v-for="item in taxSettings"
                            :key="item.key"
                            :xs="24"
                            :sm="12"
                            :md="8"
                        >
                            <el-form-item :label="item.label">
                                <el-select v-model="item.value" style="width: 100%">
                                    <el-option label="Added on top of the discounted price" :value="0" />
                                    <el-option label="Absorbed in the discounted price" :value="1" />
                                </el-select>
                            </el-form-item>
                        </el-col>
                    </el-row>
                </el-form>

                <el-button type="primary" :loading="saving" @click="saveRates">
                    Save Rate Changes
                </el-button>
            </div>
        </div>
    </div>

    <div class="card mb-3">
        <div class="card-header d-flex justify-content-between align-items-center">
            <strong>Addons</strong>
            <el-button type="primary" size="small" @click="openCreate">Add Addon Type</el-button>
        </div>
        <div class="card-body p-0">
            <el-table :data="addonTypes" stripe v-loading="loading" empty-text="No addon types">
                <el-table-column prop="name" label="Name" min-width="160" />
                <el-table-column label="Formula" width="180">
                    <template #default="{ row }">
                        {{ formulaLabel(row.formula) }}
                    </template>
                </el-table-column>
                <el-table-column label="Service Cost %" width="140">
                    <template #default="{ row }">
                        {{ formatVal(row.service_cost_percent) }}
                    </template>
                </el-table-column>
                <el-table-column label="GST %" width="120">
                    <template #default="{ row }">
                        {{ row.formula === 'separate_cascade' ? formatVal(row.gst_percent) : '—' }}
                    </template>
                </el-table-column>
                <el-table-column label="Actions" width="160" fixed="right">
                    <template #default="{ row }">
                        <el-button size="small" @click="openEdit(row)">Edit</el-button>
                        <el-button size="small" type="danger" @click="removeAddon(row)">Delete</el-button>
                    </template>
                </el-table-column>
            </el-table>
        </div>
    </div>

    <div class="card">
        <div class="card-header">
            <strong>Change Log</strong>
        </div>
        <div class="card-body p-0">
            <el-table :data="logs" stripe v-loading="loading" empty-text="No changes yet">
                <el-table-column prop="created_at" label="Date / Time" width="180" />
                <el-table-column prop="label" label="Setting" min-width="180" />
                <el-table-column prop="detail" label="Change" min-width="280" />
                <el-table-column prop="updated_by_name" label="Updated By" width="160" />
            </el-table>
        </div>
    </div>

    <el-dialog
        v-model="dialogVisible"
        :title="editingId ? 'Edit Addon Type' : 'Add Addon Type'"
        width="480px"
        destroy-on-close
    >
        <el-form label-position="top">
            <el-form-item label="Name (must match addon name on quotation)">
                <el-input v-model="dialogForm.name" maxlength="100" />
            </el-form-item>
            <el-form-item label="Formula">
                <el-select v-model="dialogForm.formula" style="width: 100%">
                    <el-option label="Land-style markup" value="land_markup" />
                    <el-option label="Separate cascade (with GST)" value="separate_cascade" />
                </el-select>
            </el-form-item>
            <el-form-item label="Service Cost (%)">
                <el-input-number
                    v-model="dialogForm.service_cost_percent"
                    :min="0"
                    :max="100"
                    :precision="2"
                    :step="0.5"
                    controls-position="right"
                    style="width: 100%"
                />
            </el-form-item>
            <el-form-item v-if="dialogForm.formula === 'separate_cascade'" label="GST (%)">
                <el-input-number
                    v-model="dialogForm.gst_percent"
                    :min="0"
                    :max="100"
                    :precision="2"
                    :step="0.5"
                    controls-position="right"
                    style="width: 100%"
                />
            </el-form-item>
        </el-form>
        <template #footer>
            <el-button @click="dialogVisible = false">Cancel</el-button>
            <el-button type="primary" :loading="dialogSaving" @click="saveAddon">Save</el-button>
        </template>
    </el-dialog>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import axios from 'axios';

export default {
    name: 'QuotationPricingSettings',
    setup() {
        const loading = ref(false);
        const saving = ref(false);
        const dialogSaving = ref(false);
        const formSettings = ref([]);
        const original = ref({});
        const addonTypes = ref([]);
        const logs = ref([]);
        const landKeys = ref([]);
        const flightKeys = ref([]);
        const automationKeys = ref([]);
        const taxKeys = ref([]);

        const dialogVisible = ref(false);
        const editingId = ref(null);
        const dialogForm = ref({
            name: '',
            formula: 'land_markup',
            service_cost_percent: 0,
            gst_percent: 0,
        });

        const formatVal = (v) => {
            if (v === null || v === undefined) return '—';
            return Number(v).toFixed(2);
        };

        const formulaLabel = (f) => {
            if (f === 'land_markup') return 'Land markup';
            if (f === 'separate_cascade') return 'Separate cascade';
            return f;
        };

        const landSettings = computed(() =>
            formSettings.value.filter((s) => landKeys.value.includes(s.key))
        );
        const flightSettings = computed(() =>
            formSettings.value.filter((s) => flightKeys.value.includes(s.key))
        );
        const automationSettings = computed(() =>
            formSettings.value.filter((s) => automationKeys.value.includes(s.key))
        );
        const taxSettings = computed(() =>
            formSettings.value.filter((s) => taxKeys.value.includes(s.key))
        );

        const load = () => {
            loading.value = true;
            axios.get('/settings/quotation-pricing/data')
                .then((res) => {
                    landKeys.value = res.data.land_keys || [];
                    flightKeys.value = res.data.flight_keys || [];
                    automationKeys.value = res.data.automation_keys || [];
                    taxKeys.value = res.data.tax_keys || [];
                    formSettings.value = (res.data.settings || []).map((s) => ({
                        key: s.key,
                        label: s.label,
                        value: Number(s.value),
                    }));
                    original.value = {};
                    formSettings.value.forEach((s) => {
                        original.value[s.key] = Number(s.value);
                    });
                    addonTypes.value = res.data.addon_types || [];
                    logs.value = res.data.logs || [];
                })
                .catch((err) => {
                    ElMessage.error(err?.response?.data?.message || 'Failed to load settings');
                })
                .finally(() => {
                    loading.value = false;
                });
        };

        const saveRates = () => {
            const changed = formSettings.value
                .filter((s) => Number(s.value) !== Number(original.value[s.key]))
                .map((s) => ({ key: s.key, value: Number(s.value) }));

            if (changed.length === 0) {
                ElMessage.info('No changes to save');
                return;
            }

            ElMessageBox.confirm(
                `Update ${changed.length} pricing rate(s)? This will apply to future quotations.`,
                'Confirm',
                { type: 'warning' }
            ).then(() => {
                saving.value = true;
                axios.post('/settings/quotation-pricing/update', { settings: changed })
                    .then((res) => {
                        ElMessage.success(res.data.message || 'Saved');
                        load();
                    })
                    .catch((err) => {
                        const msg = err?.response?.data?.message
                            || (err?.response?.data?.errors
                                ? Object.values(err.response.data.errors).flat().join(' ')
                                : 'Save failed');
                        ElMessage.error(msg);
                    })
                    .finally(() => {
                        saving.value = false;
                    });
            }).catch(() => {});
        };

        const openCreate = () => {
            editingId.value = null;
            dialogForm.value = {
                name: '',
                formula: 'land_markup',
                service_cost_percent: 0,
                gst_percent: 0,
            };
            dialogVisible.value = true;
        };

        const openEdit = (row) => {
            editingId.value = row.id;
            dialogForm.value = {
                name: row.name,
                formula: row.formula,
                service_cost_percent: Number(row.service_cost_percent),
                gst_percent: Number(row.gst_percent ?? 0),
            };
            dialogVisible.value = true;
        };

        const saveAddon = () => {
            if (!String(dialogForm.value.name || '').trim()) {
                ElMessage.error('Name is required');
                return;
            }
            const payload = {
                name: String(dialogForm.value.name).trim(),
                formula: dialogForm.value.formula,
                service_cost_percent: Number(dialogForm.value.service_cost_percent),
                gst_percent: dialogForm.value.formula === 'separate_cascade'
                    ? Number(dialogForm.value.gst_percent)
                    : null,
            };

            dialogSaving.value = true;
            const req = editingId.value
                ? axios.put(`/settings/quotation-pricing/addon-types/${editingId.value}`, payload)
                : axios.post('/settings/quotation-pricing/addon-types', payload);

            req.then((res) => {
                ElMessage.success(res.data.message || 'Saved');
                dialogVisible.value = false;
                load();
            }).catch((err) => {
                const msg = err?.response?.data?.message
                    || (err?.response?.data?.errors
                        ? Object.values(err.response.data.errors).flat().join(' ')
                        : 'Save failed');
                ElMessage.error(msg);
            }).finally(() => {
                dialogSaving.value = false;
            });
        };

        const removeAddon = (row) => {
            ElMessageBox.confirm(
                `Delete addon type "${row.name}"? Matching will stop for this name.`,
                'Confirm delete',
                { type: 'warning' }
            ).then(() => {
                axios.delete(`/settings/quotation-pricing/addon-types/${row.id}`)
                    .then((res) => {
                        ElMessage.success(res.data.message || 'Deleted');
                        load();
                    })
                    .catch((err) => {
                        ElMessage.error(err?.response?.data?.message || 'Delete failed');
                    });
            }).catch(() => {});
        };

        onMounted(load);

        return {
            loading,
            saving,
            dialogSaving,
            landSettings,
            flightSettings,
            automationSettings,
            taxSettings,
            addonTypes,
            logs,
            dialogVisible,
            editingId,
            dialogForm,
            formatVal,
            formulaLabel,
            saveRates,
            openCreate,
            openEdit,
            saveAddon,
            removeAddon,
        };
    },
};
</script>
